import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import {
    DropdownDirection,
    MultiFormSelectOptions,
    MultiFormSelectStyle,
    SortInfo,
    SortOption
} from '@stylehaven/components';

@Component({
    standalone: false,
    selector: 'digihaven-sort-info',
    templateUrl: './sort-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SortInfoComponent implements OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly sortOptions$: BehaviorSubject<SortOption[]> = new BehaviorSubject<SortOption[]>([]);

    readonly openDirection: DropdownDirection = DropdownDirection.BelowLeft;
    readonly style: MultiFormSelectStyle = MultiFormSelectStyle.Ghost;
    readonly formControl: UntypedFormControl = new UntypedFormControl();

    readonly mutliFormSelectOptions$: Observable<MultiFormSelectOptions>;

    @Input()
    set sortOptions(sortOptions: SortOption[]) {
        this.sortOptions$.next(sortOptions || []);
    }

    @Output() sortInfoChanged: EventEmitter<SortInfo | undefined> = new EventEmitter();

    constructor() {
        this.mutliFormSelectOptions$ = this.getOptionStream();

        this.propagateSelectedSortInfo();

        combineLatest([this.sortOptions$, this.mutliFormSelectOptions$])
            .pipe(
                map(([sortOptions, _]) => sortOptions.find(sortOption => sortOption.isDefault)),
                filter((sortOption): sortOption is SortOption => !!sortOption),
                takeUntil(this.destroy$)
            )
            .subscribe(sortOption => this.formControl.setValue([this.generateKey(sortOption)]));
    }

    ngOnDestroy(): void {
        this.sortOptions$.complete();

        this.destroy$.next();
        this.destroy$.complete();
    }

    private propagateSelectedSortInfo(): void {
        this.formControl.valueChanges
            .pipe(
                map((strings: string[] | null) => this.unwrapObject(strings)),
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$)
            )
            .subscribe((sortInfo: SortInfo | undefined) => this.sortInfoChanged.emit(sortInfo));
    }

    private getOptionStream(): Observable<MultiFormSelectOptions> {
        return this.sortOptions$.pipe(
            distinctUntilChanged(isEqual),
            map((sortOptions: SortOption[]) => ({
                isLiveData: false,
                options: sortOptions.map(sortOption => ({
                    label: sortOption.label,
                    value: this.generateKey(sortOption)
                }))
            }))
        );
    }

    private generateKey(sortOption: SortOption): string {
        const { label, isDefault, ...rest } = sortOption;
        return this.wrapObject(rest);
    }

    private wrapObject(obj: SortInfo): string {
        return btoa(JSON.stringify(obj));
    }

    private unwrapObject(obj: string[] | null): SortInfo | undefined {
        return obj?.length === 1 ? JSON.parse(atob(this.extraceBase64Key(obj))) : undefined;
    }

    private extraceBase64Key(obj: string[]): string {
        return obj[0];
    }
}
